import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico/rodape`;

/** O erro fica local: sem rodapé, o portal mostra só o copyright, sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Um link de `GET publico/rodape`. */
export interface LinkRodapePublicoResponse {
  desRotuloLkr: string;
  /** 'interno' (caminho do portal) ou 'externo' (URL com http/https). */
  desDestinoLkr: string;
  desLinkLkr: string;
  bitNovaAbaLkr: boolean;
}

/** Um contato de `GET publico/rodape`. */
export interface ContatoRodapePublicoResponse {
  /** 'telefone', 'email' ou 'whatsapp'. */
  desTipoCtr: string;
  desTextoCtr: string;
  /** Só dígitos no telefone e no WhatsApp; o e-mail no e-mail. */
  desValorCtr: string;
}

/**
 * A resposta de `GET publico/rodape`. Os interruptores do adm já vêm aplicados: bloco
 * desligado chega sem conteúdo (endereço nulo, lista vazia).
 */
export interface RodapePublicoResponse {
  bitExibeBrasaoRdp: boolean;
  bitExibeNomeRdp: boolean;
  bitExibeRedesRdp: boolean;
  desLogradouroRdp: string | null;
  desBairroRdp: string | null;
  desCidadeUfRdp: string | null;
  desCepRdp: string | null;
  desHorarioRdp: string | null;
  links: LinkRodapePublicoResponse[];
  contatos: ContatoRodapePublicoResponse[];
}

/** Um link do acesso rápido como o rodapé o desenha. */
export interface LinkRodapePortal {
  rotulo: string;
  /** Interno navega pelo `routerLink`; externo é `href`. */
  interno: boolean;
  link: string;
  novaAba: boolean;
}

/** Um contato como o rodapé o desenha: o ícone Lucide e o destino do link já prontos. */
export interface ContatoRodapePortal {
  icone: string;
  texto: string;
  href: string;
  novaAba: boolean;
}

/** O rodapé pronto para o template. */
export interface RodapePortal {
  exibeBrasao: boolean;
  exibeNome: boolean;
  exibeRedes: boolean;
  /** "logradouro — bairro" e "cidade/UF, CEP: cep", sem linha vazia. */
  endereco: readonly string[];
  horario: string | null;
  links: readonly LinkRodapePortal[];
  contatos: readonly ContatoRodapePortal[];
}

/**
 * Carregando ou com erro: nada além do copyright. Os interruptores ficam desligados para
 * não mostrar, nem por um instante, um brasão que o órgão desligou.
 */
export const RODAPE_VAZIO: RodapePortal = {
  exibeBrasao: false,
  exibeNome: false,
  exibeRedes: false,
  endereco: [],
  horario: null,
  links: [],
  contatos: [],
};

function limpar(texto: string | null | undefined): string {
  return texto?.trim() ?? '';
}

/** A mesma quebra da prévia do adm (`linhasDoEndereco` de `rodape.model.ts`). */
export function linhasDoEndereco(
  logradouro: string | null | undefined,
  bairro: string | null | undefined,
  cidadeUf: string | null | undefined,
  cep: string | null | undefined,
): string[] {
  const cepLimpo = limpar(cep);
  const primeira = [limpar(logradouro), limpar(bairro)].filter(Boolean).join(' — ');
  const segunda = [limpar(cidadeUf), cepLimpo ? `CEP: ${cepLimpo}` : ''].filter(Boolean).join(', ');

  return [primeira, segunda].filter((linha) => linha.length > 0);
}

/** Até 11 dígitos é DDD e número; 12 e 13 já trazem o código do país. */
function comCodigoDoPais(valor: string): string {
  const digitos = valor.replace(/\D/g, '');
  return digitos.length > 11 ? digitos : `55${digitos}`;
}

/**
 * O ícone e o destino de um contato: `tel:`, `mailto:` ou `wa.me` (em nova aba). Os ícones
 * são os de `ICONE_CONTATO` do adm. Tipo desconhecido não é exibido.
 */
export function paraContatoPortal(tipo: string, texto: string, valor: string): ContatoRodapePortal | null {
  switch (tipo) {
    case 'telefone':
      return { icone: 'phone', texto, href: `tel:+${comCodigoDoPais(valor)}`, novaAba: false };
    case 'email':
      return { icone: 'mail', texto, href: `mailto:${valor.trim()}`, novaAba: false };
    case 'whatsapp':
      return { icone: 'message-circle', texto, href: `https://wa.me/${comCodigoDoPais(valor)}`, novaAba: true };
    default:
      return null;
  }
}

export function paraRodapePortal(resposta: RodapePublicoResponse): RodapePortal {
  return {
    exibeBrasao: resposta.bitExibeBrasaoRdp,
    exibeNome: resposta.bitExibeNomeRdp,
    exibeRedes: resposta.bitExibeRedesRdp,
    endereco: linhasDoEndereco(resposta.desLogradouroRdp, resposta.desBairroRdp, resposta.desCidadeUfRdp, resposta.desCepRdp),
    horario: limpar(resposta.desHorarioRdp) || null,
    links: (resposta.links ?? []).map((link) => {
      const externo = link.desDestinoLkr === 'externo';

      return { rotulo: link.desRotuloLkr, interno: !externo, link: link.desLinkLkr, novaAba: externo && link.bitNovaAbaLkr };
    }),
    contatos: (resposta.contatos ?? [])
      .map((contato) => paraContatoPortal(contato.desTipoCtr, contato.desTextoCtr, contato.desValorCtr))
      .filter((contato): contato is ContatoRodapePortal => contato !== null),
  };
}

/**
 * O que o rodapé do órgão publica, com os interruptores do adm já aplicados pela API. É
 * raiz: o rodapé de todas as páginas faz uma requisição por carregamento do portal. O
 * portal não envia o órgão: o back-end o resolve pelo host.
 */
@Service()
export class RodapePublicoService {
  private readonly rodapeApi = httpResource<ResponseApi<RodapePublicoResponse>>(() => ({
    url: RECURSO,
    context: ERRO_LOCAL(),
  }));

  readonly carregando = computed(() => this.rodapeApi.isLoading());

  /** O `error()` é consultado antes do `value()`, que lança em estado de erro. */
  readonly rodape = computed<RodapePortal>(() => {
    if (this.rodapeApi.error() !== undefined) return RODAPE_VAZIO;

    const dados = this.rodapeApi.value()?.data;

    return dados ? paraRodapePortal(dados) : RODAPE_VAZIO;
  });
}
